import { useState, useEffect } from 'react';
import { 
  Settings, 
  Save,
  AlertTriangle,
  Shield,
  Clock,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { systemAPI } from '../lib/api';
import { toast } from 'sonner';

export default function SystemSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await systemAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await systemAPI.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">System Settings</h1>
          <p className="page-subtitle">Configure global system parameters</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Settings Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Maintenance Mode */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Maintenance Mode
            </CardTitle>
            <CardDescription>
              Enable to prevent users from accessing the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable Maintenance Mode</p>
                <p className="text-sm text-muted-foreground">
                  All users will see a maintenance page
                </p>
              </div>
              <Switch
                checked={settings?.maintenance_mode || false}
                onCheckedChange={(v) => setSettings({ ...settings, maintenance_mode: v })}
                data-testid="maintenance-mode-switch"
              />
            </div>
            {settings?.maintenance_mode && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Maintenance mode is currently active
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Game Settings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Default Game Settings
            </CardTitle>
            <CardDescription>
              Default configuration for new game tables
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Default Rake Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.1"
                  value={settings?.default_rake_percentage || 5}
                  onChange={(e) => setSettings({ ...settings, default_rake_percentage: parseFloat(e.target.value) })}
                  className="max-w-24"
                  data-testid="default-rake-input"
                />
                <span className="text-muted-foreground">%</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Default Turn Timer</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={settings?.default_turn_timer || 30}
                  onChange={(e) => setSettings({ ...settings, default_turn_timer: parseInt(e.target.value) })}
                  className="max-w-24"
                  data-testid="default-timer-input"
                />
                <span className="text-muted-foreground">seconds</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Deposit and withdrawal limits — enforced on the player app */}
        <Card className="bg-card border-border md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Deposit and withdrawal limits
            </CardTitle>
            <CardDescription>
              These amounts are enforced in the app. Pending requests count toward the daily cap. Set a daily limit to 0 for no daily cap.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Minimum deposit</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="1"
                    value={settings?.min_deposit ?? 100}
                    onChange={(e) => setSettings({ ...settings, min_deposit: parseFloat(e.target.value) })}
                    data-testid="min-deposit-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Maximum deposit</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="1"
                    value={settings?.max_deposit ?? 100000}
                    onChange={(e) => setSettings({ ...settings, max_deposit: parseFloat(e.target.value) })}
                    data-testid="max-deposit-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Daily deposit limit</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    value={settings?.daily_deposit_limit ?? 100000}
                    onChange={(e) => setSettings({ ...settings, daily_deposit_limit: parseFloat(e.target.value) })}
                    data-testid="daily-deposit-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Minimum withdrawal</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="1"
                    value={settings?.min_withdrawal ?? 100}
                    onChange={(e) => setSettings({ ...settings, min_withdrawal: parseFloat(e.target.value) })}
                    data-testid="min-withdrawal-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Maximum withdrawal</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="1"
                    value={settings?.max_withdrawal ?? 100000}
                    onChange={(e) => setSettings({ ...settings, max_withdrawal: parseFloat(e.target.value) })}
                    data-testid="max-withdrawal-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Daily withdrawal limit</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    value={settings?.daily_withdrawal_limit ?? 100000}
                    onChange={(e) => setSettings({ ...settings, daily_withdrawal_limit: parseFloat(e.target.value) })}
                    data-testid="daily-withdrawal-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="font-medium">Require KYC for withdrawal</p>
                <p className="text-sm text-muted-foreground">
                  Players cannot withdraw any amount until KYC is approved. Approving a payout is also blocked.
                </p>
              </div>
              <Switch
                checked={settings?.kyc_required_for_withdrawal !== false}
                onCheckedChange={(v) => setSettings({ ...settings, kyc_required_for_withdrawal: v })}
                data-testid="kyc-required-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Security Settings
            </CardTitle>
            <CardDescription>
              Configure security parameters
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">2FA for Admins</p>
                <p className="text-sm text-muted-foreground">
                  Require 2FA for all admin accounts
                </p>
              </div>
              <Switch defaultChecked data-testid="2fa-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">IP Whitelisting</p>
                <p className="text-sm text-muted-foreground">
                  Restrict admin access to whitelisted IPs
                </p>
              </div>
              <Switch data-testid="ip-whitelist-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Suspicious Activity Alerts</p>
                <p className="text-sm text-muted-foreground">
                  Send alerts for suspicious activities
                </p>
              </div>
              <Switch defaultChecked data-testid="suspicious-alerts-switch" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* API Keys Section */}
      <Card className="bg-card border-border mt-6">
        <CardHeader>
          <CardTitle>API Configuration</CardTitle>
          <CardDescription>
            Manage third-party API integrations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Payment Gateway API Key</Label>
              <Input
                type="password"
                placeholder="••••••••••••••••"
                disabled
                data-testid="payment-api-key"
              />
              <p className="text-xs text-muted-foreground">Razorpay / Cashfree integration</p>
            </div>
            <div className="space-y-2">
              <Label>SMS Gateway API Key</Label>
              <Input
                type="password"
                placeholder="••••••••••••••••"
                disabled
                data-testid="sms-api-key"
              />
              <p className="text-xs text-muted-foreground">Twilio / MSG91 integration</p>
            </div>
            <div className="space-y-2">
              <Label>Email Service API Key</Label>
              <Input
                type="password"
                placeholder="••••••••••••••••"
                disabled
                data-testid="email-api-key"
              />
              <p className="text-xs text-muted-foreground">SendGrid / AWS SES integration</p>
            </div>
            <div className="space-y-2">
              <Label>Push Notification Key</Label>
              <Input
                type="password"
                placeholder="••••••••••••••••"
                disabled
                data-testid="push-api-key"
              />
              <p className="text-xs text-muted-foreground">Firebase / OneSignal integration</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            API keys are encrypted and cannot be viewed. Contact system administrator to update.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
